import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { useAuth } from "../auth/useAuth";
import type { UserPick } from "../../api/types";
import type { FormDef, WorkOrder } from "../../api/workorders";
import { WorkOrderEditor } from "./WorkOrderEditor";

// 路由殼:/workorders/new?form=(新增:先選種類)與 /workorders/:id。

function FormChooser({ forms, onPick }: { forms: FormDef[]; onPick: (code: string) => void }) {
  return (
    <div className="flex flex-col gap-3">
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">新增工單</h2>
      <p className="m-0 text-sm text-[var(--text-2)]">先選工單種類。</p>
      <div className="flex flex-wrap gap-2">
        {forms.map((f) => (
          <Button key={f.code} type="button" variant="secondary" onClick={() => onPick(f.code)}>{f.name}</Button>
        ))}
      </div>
    </div>
  );
}

export function WorkOrderEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === undefined;
  const [forms, setForms] = useState<FormDef[]>([]);
  // null = 還沒載入。編輯器要等名單到了才畫:承辦人下拉要對照「可選的人」。
  const [users, setUsers] = useState<UserPick[] | null>(null);
  const [usersFailed, setUsersFailed] = useState(false);
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    api.get<FormDef[]>("/work-orders/forms").then((got) => setForms(Array.isArray(got) ? got : [])).catch(() => setFailed("工單種類載入失敗,請重新整理"));
    api.get<UserPick[]>("/users/pickable").then((got) => setUsers(Array.isArray(got) ? got : [])).catch(() => {
      setUsers([]);
      setUsersFailed(true);
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    api.get<WorkOrder>(`/work-orders/${id}`)
      .then((got) => alive && setWorkOrder(got))
      .catch((e) => alive && setFailed(e instanceof Error ? e.message : "找不到這張工單"));
    return () => {
      alive = false;
    };
  }, [id, isNew]);

  const formCode = isNew ? params.get("form") : workOrder?.form_type ?? null;
  const form = forms.find((f) => f.code === formCode);

  if (failed) return <p role="alert" className="m-0 text-sm text-[var(--danger)]">{failed}</p>;
  if (isNew && (!formCode || (forms.length > 0 && !form))) {
    return <FormChooser forms={forms} onPick={(code) => navigate(`/workorders/new?form=${code}`)} />;
  }
  if (!form || users === null || (!isNew && !workOrder)) return <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Link to="/workorders" className="flex items-center gap-1 text-sm text-[var(--text-2)] no-underline hover:text-[var(--text)]">
        <ArrowLeft aria-hidden="true" size={14} />
        回製單
      </Link>
      <WorkOrderEditor
        key={workOrder?.id ?? "new"}
        form={form}
        workOrder={isNew ? null : workOrder}
        users={users}
        currentUserId={user?.id ?? null}
        usersFailed={usersFailed}
        onOpenWorkOrder={(next) => navigate(`/workorders/${next}`)}
        onDeleted={() => navigate("/workorders")}
        onSaved={(got) => {
          setWorkOrder(got);
          if (isNew) navigate(`/workorders/${got.id}`, { replace: true });
        }}
      />
    </div>
  );
}
